import {
  StudyStakeholderRole,
  type StudyStakeholderSelectDto,
  type StudyStakeholderUserSelectDto,
} from '@@types/api.types';
import { Modal } from '@components/core/modal.component';
import { Avatar } from '@components/shared/avatar.component';
import { ConfirmDeleteModal } from '@components/shared/confirm-delete-modal.component';
import { Button } from '@components/ui/button';
import { useAppForm } from '@components/ui/form/form';
import { errorToast, successToast } from '@hooks/toast.hook';
import {
  useCreateMeiaStudyStakeholder,
  useDeleteMeiaStudyStakeholder,
  useGetMeiaStudyStakeholderCandidates,
  useGetMeiaStudyStakeholders,
  useUpdateMeiaStudyStakeholder,
} from '@queries/meia.query';
import { type MeiaStakeholderFormValues, meiaStakeholderSchema } from '@schemas/meia.schema';
import { createFormInvalidHandler } from '@utils/form.util';
import { SelectOption } from '@utils/select.util';
import { Trash2Icon } from 'lucide-react';
import { type FC, useMemo, useState } from 'react';
import { MEIA_AUTOFILL_IGNORE_PROPS } from './meia-form-autofill.util';

type MeiaStakeholderModalProps = {
  defaultRole?: StudyStakeholderRole;
  isOpen: boolean;
  onClose: () => void;
  stakeholder?: StudyStakeholderSelectDto;
  studyId: string;
};

export const MeiaStakeholderModal: FC<MeiaStakeholderModalProps> = (props) => {
  const { defaultRole, isOpen, onClose, stakeholder, studyId } = props;
  const isEdit = Boolean(stakeholder);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  const { isPending: isCreating, mutateAsync: createStakeholder } = useCreateMeiaStudyStakeholder();
  const { isPending: isUpdating, mutateAsync: updateStakeholder } = useUpdateMeiaStudyStakeholder();
  const { mutateAsync: deleteStakeholder } = useDeleteMeiaStudyStakeholder();
  const { data: candidates = [] } = useGetMeiaStudyStakeholderCandidates({ studyId });
  const { data: existingStakeholders = [] } = useGetMeiaStudyStakeholders({ studyId });

  const defaultValues: MeiaStakeholderFormValues = {
    organization: stakeholder?.organization ?? null,
    responsibility: stakeholder?.responsibility ?? null,
    role: stakeholder?.role ?? defaultRole ?? StudyStakeholderRole.REVIEWER,
    userId: stakeholder?.userId ?? '',
  };

  const form = useAppForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      const validationResult = meiaStakeholderSchema.safeParse(value);
      if (!validationResult.success) {
        errorToast({ description: 'Please complete stakeholder details.' });
        return;
      }

      if (stakeholder) {
        const { organization, responsibility, role } = validationResult.data;
        await updateStakeholder({
          organization,
          responsibility,
          role,
          stakeholderId: stakeholder.id,
          studyId,
        });
        successToast({ description: 'Stakeholder updated.' });
      } else {
        await createStakeholder({ ...validationResult.data, studyId });
        successToast({ description: 'Stakeholder added.' });
      }
      onClose();
    },
    onSubmitInvalid: createFormInvalidHandler<typeof defaultValues>(),
    validators: { onSubmit: meiaStakeholderSchema },
  });

  const existingStakeholderUserIds = useMemo(() => {
    return new Set(existingStakeholders.map((existingStakeholder) => existingStakeholder.userId));
  }, [existingStakeholders]);

  const userOptions = useMemo(() => {
    const usersById = new Map<string, StudyStakeholderUserSelectDto>();

    for (const candidate of candidates) {
      usersById.set(candidate.id, candidate);
    }

    if (stakeholder?.user) {
      usersById.set(stakeholder.user.id, stakeholder.user);
    }

    return Array.from(usersById.values())
      .filter((user) => {
        if (stakeholder?.userId === user.id) return true;
        return !existingStakeholderUserIds.has(user.id);
      })
      .map((user) => {
        const displayName = formatStakeholderUserDisplayName(user);
        const title = user.profile?.title;
        return {
          label: title ? `${displayName} ${user.email} ${title}` : `${displayName} ${user.email}`,
          value: user.id,
        };
      });
  }, [candidates, existingStakeholderUserIds, stakeholder]);

  const handleConfirmDelete = async (): Promise<void> => {
    if (!stakeholder) return;
    await deleteStakeholder({ stakeholderId: stakeholder.id, studyId });
    successToast({ description: 'Stakeholder deleted.' });
    onClose();
  };

  const isSaving = isCreating || isUpdating;
  const renderUserOption = (option: SelectOption) => {
    const user = resolveStakeholderUser({
      candidates,
      option,
      stakeholder,
    });
    if (!user) return option.label;

    const displayName = formatStakeholderUserDisplayName(user);

    return (
      <div className="flex min-w-0 items-center gap-2">
        <Avatar
          altText={displayName}
          imageUrl={user.avatarUrl}
          initialText={displayName}
          size="sm"
        />
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-sm font-medium">{displayName}</span>
          {user.profile?.title && <span className="truncate text-xs text-gray-500">{user.profile.title}</span>}
        </div>
      </div>
    );
  };

  return (
    <>
      <Modal
        centered
        footer={{
          leftButton:
            isEdit && stakeholder?.role !== StudyStakeholderRole.OWNER ? (
              <Button
                onClick={() => setIsConfirmingDelete(true)}
                type="button"
                variant="destructive"
              >
                <Trash2Icon className="size-4" />
                Delete
              </Button>
            ) : undefined,
          rightButton: {
            children: isSaving ? 'Saving...' : isEdit ? 'Save' : 'Add Stakeholder',
            disabled: isSaving,
            onClick: () => void form.handleSubmit(),
          },
        }}
        header={{
          title: isEdit ? 'Edit Stakeholder' : 'Add Stakeholder',
        }}
        isOpen={isOpen}
        onClose={onClose}
        width="640px"
      >
        <form
          {...MEIA_AUTOFILL_IGNORE_PROPS}
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <form.AppForm>
            <div className="grid gap-3 md:grid-cols-2">
              <form.AppField name="userId">
                {(field) => (
                  <field.SelectPopoverField
                    allowClear={!isEdit}
                    disabled={isEdit}
                    emptyMessage="No Intelligence users available"
                    formatValue={(option) => formatStakeholderUserOptionValue({ candidates, option, stakeholder })}
                    label="User"
                    options={userOptions}
                    placeholder="Select Intelligence user"
                    renderOption={renderUserOption}
                    required
                    searchPlaceholder="Search Intelligence users..."
                  />
                )}
              </form.AppField>
              <form.AppField name="role">
                {(field) => (
                  <field.SelectField
                    label="Role"
                    options={STAKEHOLDER_ROLE_OPTIONS}
                    placeholder="Select role"
                    required
                  />
                )}
              </form.AppField>
              <form.AppField name="organization">
                {(field) => (
                  <field.TextField
                    {...MEIA_AUTOFILL_IGNORE_PROPS}
                    className="md:col-span-2"
                    label="Organization"
                    placeholder="USINDOPACOM J5"
                  />
                )}
              </form.AppField>
              <form.AppField name="responsibility">
                {(field) => (
                  <field.TextAreaField
                    className="md:col-span-2"
                    label="Responsibility"
                    placeholder="Approves operational risk and signs off on the mission's success criteria."
                    rows={3}
                  />
                )}
              </form.AppField>
            </div>
          </form.AppForm>
        </form>
      </Modal>
      {stakeholder && (
        <ConfirmDeleteModal
          isOpen={isConfirmingDelete}
          itemName="stakeholder"
          onConfirm={handleConfirmDelete}
          setIsOpen={setIsConfirmingDelete}
        />
      )}
    </>
  );
};

// AZ-451 (D4): assignable Study Roles are Decision Maker of Record, Reviewer, Sponsor. The Author/Lead
// (OWNER) is derived from ownership and Mission Engineer is now an org persona — neither is assignable.
const STAKEHOLDER_ROLE_OPTIONS = [
  { label: 'Decision Maker of Record', value: StudyStakeholderRole.DECISION_MAKER },
  { label: 'Reviewer', value: StudyStakeholderRole.REVIEWER },
  { label: 'Sponsor', value: StudyStakeholderRole.SPONSOR },
];

const formatStakeholderUserDisplayName = (user: {
  email: string;
  profile?: { firstName: string; lastName: string };
}): string => {
  const fullName = user.profile ? `${user.profile.firstName} ${user.profile.lastName}`.trim() : '';
  return fullName || user.email;
};

const formatStakeholderUserOptionValue = (params: {
  candidates: StudyStakeholderUserSelectDto[];
  option: SelectOption;
  stakeholder?: StudyStakeholderSelectDto;
}): string => {
  const user = resolveStakeholderUser(params);
  return user ? formatStakeholderUserDisplayName(user) : params.option.label;
};

const resolveStakeholderUser = (params: {
  candidates: StudyStakeholderUserSelectDto[];
  option: SelectOption;
  stakeholder?: StudyStakeholderSelectDto;
}): StudyStakeholderUserSelectDto | undefined => {
  const { candidates, option, stakeholder } = params;

  if (stakeholder?.user.id === option.value) {
    return stakeholder.user;
  }

  return candidates.find((candidate) => candidate.id === option.value);
};
